import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updateAISettingsSchema, type AIConfig } from '@octery/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** "123, 456" → [123, 456]; non-numbers are reported by the schema check. */
function parseIds(value: string): number[] {
  return value
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number);
}

function parseTags(value: string): string[] {
  return value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

/**
 * Lead rollout (test mode): limits the bot to a share of leads and/or to
 * leads in given amoCRM statuses or with given deal tags. 100% and empty
 * lists = the bot handles every lead.
 */
export function LeadRolloutCard({ config }: { config: AIConfig | undefined }) {
  const queryClient = useQueryClient();
  const [percent, setPercent] = useState('100');
  const [statusIds, setStatusIds] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;
    setPercent(String(config.rolloutPercent ?? 100));
    setStatusIds((config.rolloutStatusIds ?? []).join(', '));
    setTags((config.rolloutTags ?? []).join(', '));
  }, [config]);

  const mutation = useMutation({
    mutationFn: (data: { rolloutPercent: number; rolloutStatusIds: number[]; rolloutTags: string[] }) =>
      api.patch<AIConfig>('/api/ai-settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      toast.success('Test mode saved');
    },
    onError: () => {
      toast.error('Failed to save test mode');
    },
  });

  const onSave = () => {
    const data = {
      rolloutPercent: Number(percent),
      rolloutStatusIds: parseIds(statusIds),
      rolloutTags: parseTags(tags),
    };
    const check = updateAISettingsSchema.safeParse(data);
    if (!check.success) {
      setError('Percent must be 0–100, status IDs must be positive whole numbers.');
      return;
    }
    setError(null);
    mutation.mutate(data);
  };

  const limited = Number(percent) < 100 || parseIds(statusIds).length > 0 || parseTags(tags).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Test mode (lead rollout)</CardTitle>
        <CardDescription>
          Let the bot answer only part of your leads. 100% with empty lists means every lead.
          Leads outside the selection are left to managers: the bot does not reply or move them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium">Share of leads, %</label>
            <Input
              type="number"
              min={0}
              max={100}
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The same lead always stays in or out; raising the share keeps selected leads.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">amoCRM status IDs</label>
            <Input
              placeholder="61234567, 61234568"
              value={statusIds}
              onChange={(e) => setStatusIds(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Status IDs from the amoCRM pipeline settings, comma-separated.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Deal tags</label>
            <Input placeholder="ai_test" value={tags} onChange={(e) => setTags(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              A lead matches with any of these tags or statuses.
            </p>
          </div>
        </div>
        {error && <p className="text-destructive text-xs">{error}</p>}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {limited ? 'Test mode is on: the bot answers only selected leads.' : 'Test mode is off.'}
          </p>
          <Button type="button" onClick={onSave} disabled={mutation.isPending || !config}>
            {mutation.isPending ? 'Saving...' : 'Save test mode'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
